import { useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { supportedLanguages, defaultLanguage } from '@/config/routes';

/**
 * LanguageRouter component that:
 * 1. Extracts language from URL
 * 2. Syncs URL language with context
 * 3. Redirects invalid languages to default
 */
export const LanguageRouter = ({ children }) => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    // If lang param exists and is valid, sync with context
    if (lang && supportedLanguages.includes(lang)) {
      if (lang !== language) {
        // URL navigation is not a user choice, just sync the language
        setLanguage(lang, false);
      }
    } 
    // If lang param is invalid, redirect to default language
    else if (lang && !supportedLanguages.includes(lang)) {
      const newPath = `/${defaultLanguage}${location.pathname}`;
      navigate(newPath, { replace: true });
    }
  }, [lang, language, setLanguage, navigate, location.pathname]);

  return children || <Outlet />;
};

/**
 * Redirect component for legacy routes without language prefix
 * Redirects /diensten to /nl/diensten based on current language
 */
export const LegacyRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    // Get the path without leading slash
    const path = location.pathname.slice(1);
    // Redirect to language-prefixed version
    navigate(`/${language}/${path}`, { replace: true });
  }, [navigate, location.pathname, language]);

  return null;
};

/**
 * Home redirect - redirects / to /{lang}
 */
export const HomeRedirect = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    navigate(`/${language}`, { replace: true });
  }, [navigate, language]);

  return null;
};
